import type { SagaIterator, SagaMiddleware, Task } from 'redux-saga';
import { Assert } from '@team/assert';
import type { BaseLogger } from '@team/logger';
import type { SagaName } from './types';

export class SagaManager {
  #middleware: SagaMiddleware;
  #registry = new Map<SagaName, Task>();
  #logger: BaseLogger;

  public constructor(middleware: SagaMiddleware, logger: BaseLogger = console) {
    this.#middleware = middleware;
    this.#logger = logger;
  }

  run(name: SagaName, saga: () => SagaIterator) {
    if (this.#registry.has(name)) {
      this.#logger.warn(`\`${name}\` is already injected. Canceling...`);

      this.cancel(name);
    }

    const task = this.#middleware.run(saga);

    this.#registry.set(name, task);

    this.#logger.info(`\`${name}\` injected`);

    return task;
  }

  get(name: SagaName) {
    return this.#registry.get(name);
  }

  cancel(name: SagaName) {
    const task = this.#registry.get(name);

    if (Assert.isUndefined(task)) {
      this.#logger.warn(`\`${name}\` not found`);

      return;
    }

    task.cancel();
    this.#registry.delete(name);

    this.#logger.warn(`\`${name}\` canceled`);
  }

  cancelAll() {
    this.#registry.forEach((task) => {
      task.cancel();
    });

    this.#registry.clear();
  }
}
